import type { components } from '@paige/api-client';
import { isReasonCode, type ReasonCode } from '@paige/format';

export type Problem = components['schemas']['Problem'];

/** An API failure carrying the RFC 9457 problem so screens can show the typed reason. */
export class ApiError extends Error {
  readonly status: number;
  readonly reason: ReasonCode | null;
  /** The problem's stable code, when the refusal is one the app acts on. */
  readonly code: Problem['code'] | null;
  readonly detail: string | null;
  /** The problem's title: the status's phrase, or the refusal's name where the API sets one (`AlreadyOnPackage`). */
  readonly title: string | null;
  readonly requestId: string | null;

  constructor(status: number, problem: Partial<Problem> | null) {
    const p: Partial<Problem> = problem ?? {};
    // `detail` first, `title` second: the API's sentence for this occurrence is the detail, and the
    // title is the status's generic phrase ("Conflict") or the problem type's name, which reads as a
    // machine token on a screen. Every other reader of a Paige problem does the same
    // (packages/capturepoint upload.ts and destination-channel.ts, screens/review/EngineOpening.tsx).
    super(p.detail ?? p.title ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.reason = reasonOf(p);
    this.code = p.code ?? null;
    this.detail = p.detail ?? null;
    this.title = p.title ?? null;
    this.requestId = p.requestId ?? null;
  }

  get unauthenticated(): boolean {
    return this.status === 401;
  }

  /** The sign-in's email address must be proven first: Paige mails a code and the sign-in is sent again with it. */
  get emailUnproven(): boolean {
    return this.status === 403 && this.code === 'email_not_verified';
  }

  /** The mailed link for a new password is wrong, old, used or replaced by a newer one. */
  get resetLinkExpired(): boolean {
    return this.status === 410 && this.code === 'reset_link_expired';
  }

  /** The company requires two-step sign-in and this sign-in or session has not completed one. */
  get secondFactorRequired(): boolean {
    return this.status === 403 && this.code === 'mfa_required';
  }
}

function reasonOf(p: Partial<Problem>): ReasonCode | null {
  return p.reason && isReasonCode(p.reason) ? p.reason : null;
}

interface Result<T> {
  data?: T | undefined;
  error?: unknown;
  response: Response;
}

/** Turns an openapi-fetch result into data or an ApiError; 204 yields undefined. */
export function unwrap<T>(r: Result<T>): T {
  if (r.response.ok) return r.data as T;
  const problem = typeof r.error === 'object' && r.error !== null ? (r.error as Problem) : null;
  throw new ApiError(r.response.status, problem);
}

/** Header params for a tenant-scoped call. */
export function tenantHeader(tenant: string): { 'Paige-Tenant': string } {
  return { 'Paige-Tenant': tenant };
}

/** A multipart body for file uploads; openapi-fetch sends FormData as-is. */
export function filesForm(files: File[]): FormData {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  return form;
}
